import {
  applyLessonTranslationsToRow,
  parseLessonTranslationsPart,
  rowToLessonTranslations,
  serializeLessonTranslationsPart,
} from "site/services/lesson-translations";
import { normalizeAudioStoragePath } from "site/utils/audio-path";
import { capitalizeRussianText, normalizeRussianTranslation } from "site/utils/russian-text";

export { capitalizeRussianText, normalizeRussianTranslation };

const BLANK_MARKER = "#...#";

const ZEPTO_TYPE_LABELS: Record<number, string> = {
  1: "Слово",
  2: "Предложение",
  3: "Задание",
  4: "Диалог",
  5: "Сборка",
  6: "Выбор формы",
  7: "Правило",
};

export const ZEPTO_TYPE_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const;

export type TaskSentenceSegment = {
  kind: "text" | "blank";
  value: string;
};

export type ZeptoRowEdit = {
  type: number;
  rowId: number;
  arabic: string;
  russian: string;
  translationEn: string;
  translationFr: string;
  ruleId: string;
  taskAnswer: string;
  taskSentence: string;
  ttsVoice: string;
  audioPath?: string;
  imagePath?: string;
};

export type ZeptoParseFailure = {
  sourceIndex: number;
  rowId: number | null;
  raw: unknown;
};

export type ZeptoRowsParseResult = {
  rows: ZeptoRowEdit[];
  failures: ZeptoParseFailure[];
};

function isKnownZeptoType(type: number): boolean {
  return Number.isFinite(type) && type >= 1 && type <= 7;
}

export function isValidZeptoType(type: number): boolean {
  return isKnownZeptoType(type);
}

function hasBuildZeptoMarkup(text: string): boolean {
  return /\|\*/.test(text) || /\|\|/.test(text);
}

function hasTaskBlankMarkup(text: string): boolean {
  return text.includes(BLANK_MARKER);
}

function hasPipeCharacter(text: string): boolean {
  return text.includes("|");
}

export function ruleIdFromZeptoContent(content: unknown): string {
  if (typeof content === "string") {
    return content.trim();
  }
  if (!Array.isArray(content)) {
    return "";
  }
  const first = content[0];
  if (typeof first === "string") {
    return first.trim();
  }
  if (Array.isArray(first) && typeof first[0] === "string") {
    return first[0].trim();
  }
  return "";
}

function textFromContentPart(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value.join("\n");
  }
  return "";
}

function applyTranslationsFromContentPart(row: ZeptoRowEdit, value: unknown): void {
  const translations = parseLessonTranslationsPart(value);
  applyLessonTranslationsToRow(row, translations);
}

function serializeRowTranslationsPart(row: ZeptoRowEdit): string[] {
  const translations = rowToLessonTranslations(row);
  return serializeLessonTranslationsPart(translations, (text) => normalizeRussianTranslation(text, row.type));
}

function stripBuildMarkers(text: string): string {
  const match = text.match(/\|\*(.+)\|\|/);
  if (match) {
    return match[1]
      .split("*")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" ");
  }
  return text.replace(/\|/g, "").trim();
}

export function taskAnswerParts(answer: string): string[] {
  if (!answer) {
    return [];
  }
  return answer.split("#");
}

export function countTaskBlanks(sentence: string): number {
  if (!sentence) {
    return 0;
  }
  return sentence.split(BLANK_MARKER).length - 1;
}

export function fillTaskBlanks(sentence: string, answer: string): string {
  if (!sentence.includes(BLANK_MARKER)) {
    return sentence;
  }
  const parts = taskAnswerParts(answer);
  let partIndex = 0;
  let result = sentence;
  while (result.includes(BLANK_MARKER)) {
    const replacement =
      parts.length > 1
        ? parts[Math.min(partIndex, parts.length - 1)] ?? ""
        : answer;
    result = result.replace(BLANK_MARKER, replacement);
    partIndex += 1;
  }
  return result;
}

export function splitTaskSentence(
  sentence: string,
  selectedAnswer: string,
): TaskSentenceSegment[] {
  const parts = taskAnswerParts(selectedAnswer);
  const segments: TaskSentenceSegment[] = [];
  let rest = sentence;
  let blankIndex = 0;

  while (rest.length) {
    const pos = rest.indexOf(BLANK_MARKER);
    if (pos === -1) {
      if (rest) {
        segments.push({ kind: "text", value: rest });
      }
      break;
    }

    if (pos > 0) {
      segments.push({ kind: "text", value: rest.slice(0, pos) });
    }

    const blankValue = selectedAnswer
      ? parts.length > 1
        ? parts[Math.min(blankIndex, parts.length - 1)] ?? ""
        : selectedAnswer
      : "";

    segments.push({ kind: "blank", value: blankValue });
    blankIndex += 1;
    rest = rest.slice(pos + BLANK_MARKER.length);
  }

  return segments;
}

export function zeptoTypeLabel(type: number): string {
  return ZEPTO_TYPE_LABELS[type] ?? `Тип ${type}`;
}

export function zeptoTypeOptionText(type: number): string {
  return `${type} — ${zeptoTypeLabel(type)}`;
}

export function deriveLessonAudioMap(
  rawLesson: Record<string, string>,
  vocabPaths: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [rowKey, path] of Object.entries(rawLesson)) {
    const vocabPath = vocabPaths[rowKey] ?? "";
    const lessonPath = normalizeAudioStoragePath(path);
    const vocabularyPath = normalizeAudioStoragePath(vocabPath);
    if (!vocabularyPath || lessonPath !== vocabularyPath) {
      out[rowKey] = path;
    }
  }
  return out;
}

export function buildEditorAudioMap(
  rows: ZeptoRowEdit[],
  prev: Record<string, string>,
  uploadedByRowId: Map<number, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = String(row.rowId);
    const uploaded = uploadedByRowId.get(row.rowId);
    if (uploaded) {
      out[key] = uploaded;
      continue;
    }
    const existing = prev[key];
    if (existing) {
      out[key] = existing;
      continue;
    }
    if (row.type !== 1 && row.audioPath) {
      out[key] = row.audioPath;
    }
  }
  return out;
}

export function buildEditorImagesMap(
  rows: ZeptoRowEdit[],
  prev: Record<string, string>,
  uploadedByRowId: Map<number, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = String(row.rowId);
    const uploaded = uploadedByRowId.get(row.rowId);
    if (uploaded) {
      out[key] = uploaded;
      continue;
    }
    if (row.imagePath) {
      out[key] = row.imagePath;
      continue;
    }
    const existing = prev[key];
    if (existing) {
      out[key] = existing;
    }
  }
  return out;
}

function dialogSpeakerLabelEnd(line: string): number {
  return line.search(/[:：]/);
}

function isDialogSpeakerLabelLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) {
    return false;
  }
  if (/^\s*лицо\s*:\s*$/i.test(trimmed)) {
    return true;
  }
  const labelEnd = dialogSpeakerLabelEnd(trimmed);
  return labelEnd >= 0 && !trimmed.slice(labelEnd + 1).trim();
}

function stripDialogSpeakerPrefix(line: string): string {
  const trimmed = line.trim();
  const labelEnd = dialogSpeakerLabelEnd(trimmed);
  if (labelEnd >= 0) {
    return trimmed.slice(labelEnd + 1).trim();
  }
  return trimmed;
}

export function resolveArabicExerciseMarkup(text: string): string {
  let out = text.trim();
  if (!out) {
    return "";
  }

  out = stripDialogSpeakerPrefix(out);

  out = out.replace(/\|\*([^|]*)\|\|(.*)$/g, (_, parts, suffix) => {
    const built = parts
      .split("*")
      .map((part: string) => part.trim())
      .filter(Boolean)
      .join(" ");
    const tail = String(suffix ?? "").trim();
    return tail ? `${built} ${tail}` : built;
  });

  out = out.replace(/\|([^|]+)\|/g, (_, markerValue: string) => {
    const correct: string[] = [];
    for (const match of markerValue.matchAll(/\+([^+-]+)/g)) {
      const word = match[1]?.trim();
      if (word) {
        correct.push(word);
      }
    }
    return correct.join(" ");
  });

  out = out.replace(/\|/g, "");
  return out.replace(/\s+/g, " ").trim();
}

function stripParentheticalForTts(text: string): string {
  return text
    .replace(/\([^)]*\)/g, " ")
    .replace(/（[^）]*）/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveArabicTtsLine(line: string, blankAnswer?: string): string {
  let text = stripDialogSpeakerPrefix(line.trim());
  if (!text) {
    return "";
  }
  if (blankAnswer && text.includes(BLANK_MARKER)) {
    text = fillTaskBlanks(text, blankAnswer);
  }
  return stripParentheticalForTts(resolveArabicExerciseMarkup(text));
}

function dialogArabicSourceLines(row: ZeptoRowEdit): string[] {
  const source = row.type === 3 ? row.taskSentence : row.arabic;
  return source
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !isDialogSpeakerLabelLine(line));
}

export function getArabicTtsDialogLines(row: ZeptoRowEdit): string[] {
  const blankAnswer = row.type === 3 ? row.taskAnswer.trim() : "";
  return dialogArabicSourceLines(row)
    .map((line) => resolveArabicTtsLine(line, blankAnswer || undefined))
    .filter(Boolean);
}

export function getArabicTextForTts(row: ZeptoRowEdit): string {
  if (row.type === 7) {
    return "";
  }
  if (row.type === 3) {
    if (!row.taskSentence.trim()) {
      return "";
    }
    const blankAnswer = row.taskAnswer.trim();
    const filled = blankAnswer
      ? fillTaskBlanks(row.taskSentence, blankAnswer)
      : row.taskSentence;
    return dialogArabicSourceLines({ ...row, taskSentence: filled, arabic: filled })
      .map((line) => resolveArabicTtsLine(line))
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  const text = row.arabic.trim();
  if (!text) {
    return "";
  }

  return dialogArabicSourceLines(row)
    .map((line) => resolveArabicTtsLine(line))
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function normalizeZepto(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw == null) {
    return [];
  }
  throw new Error("zepto must be an array");
}

function rowIdFromRaw(raw: unknown): number | null {
  if (!Array.isArray(raw) || raw.length < 3) {
    return null;
  }
  const rowId = Number(raw[2]);
  return Number.isFinite(rowId) ? rowId : null;
}

export function zeptoRowListPosition(rows: ZeptoRowEdit[], rowId: number): number {
  return rows.findIndex((row) => row.rowId === rowId);
}

export function zeptoRowDisplayNumber(rows: ZeptoRowEdit[], rowId: number): number {
  const pos = zeptoRowListPosition(rows, rowId);
  return pos >= 0 ? pos + 1 : 0;
}

export function parseZeptoRow(
  raw: unknown,
  audioRef?: string,
  imageRef?: string,
): ZeptoRowEdit | null {
  if (!Array.isArray(raw) || raw.length < 3) {
    return null;
  }

  const type = Number(raw[0]);
  const content = raw[1];
  const rowId = Number(raw[2]);

  if (!isKnownZeptoType(type) || !Number.isFinite(rowId)) {
    return null;
  }

  const base: ZeptoRowEdit = {
    type,
    rowId,
    arabic: "",
    russian: "",
    translationEn: "",
    translationFr: "",
    ruleId: "",
    taskAnswer: "",
    taskSentence: "",
    ttsVoice: "",
    audioPath: audioRef,
    imagePath: imageRef,
  };

  if (type === 3) {
    const block0 = Array.isArray(content) && Array.isArray(content[0]) ? content[0] : [];
    const taskAnswer = String(block0[0] ?? "");
    const taskSentence = String(block0[1] ?? "");
    const translations = Array.isArray(content) && Array.isArray(content[1]) ? content[1] : content?.[1];
    const row = {
      ...base,
      taskAnswer,
      taskSentence,
    };
    applyTranslationsFromContentPart(row, translations);
    return row;
  }

  if (type === 7) {
    return {
      ...base,
      ruleId: ruleIdFromZeptoContent(content),
    };
  }

  const row = {
    ...base,
    arabic: textFromContentPart(Array.isArray(content) ? content[0] : ""),
  };
  applyTranslationsFromContentPart(row, Array.isArray(content) ? content[1] : "");
  return row;
}

export function serializeZeptoRow(row: ZeptoRowEdit): unknown {
  if (row.type === 3) {
    const content: unknown[] = [
      [row.taskAnswer, row.taskSentence],
      serializeRowTranslationsPart(row),
    ];
    return [3, content, row.rowId];
  }

  if (row.type === 7) {
    return [7, row.ruleId.trim(), row.rowId];
  }

  const content: unknown[] = [row.arabic, serializeRowTranslationsPart(row)];
  return [row.type, content, row.rowId];
}

export function zeptoToRows(
  zepto: unknown,
  audio?: Record<string, string> | null,
  images?: Record<string, string> | null,
): ZeptoRowsParseResult {
  const list = normalizeZepto(zepto);
  const audioMap = audio ?? {};
  const imagesMap = images ?? {};
  const rows: ZeptoRowEdit[] = [];
  const failures: ZeptoParseFailure[] = [];
  for (let sourceIndex = 0; sourceIndex < list.length; sourceIndex += 1) {
    const item = list[sourceIndex];
    const rowId = rowIdFromRaw(item);
    const audioPath = rowId != null ? audioMap[String(rowId)] : "";
    const imagePath = rowId != null ? imagesMap[String(rowId)] : "";
    const parsed = parseZeptoRow(item, audioPath, imagePath);
    if (parsed) {
      rows.push(parsed);
    } else {
      failures.push({ sourceIndex, rowId, raw: item });
    }
  }
  return { rows, failures };
}

export function rowsToZepto(
  rows: ZeptoRowEdit[],
  failures: ZeptoParseFailure[] = [],
): unknown[] {
  if (!failures.length) {
    return rows.map((row) => serializeZeptoRow(row));
  }
  const failureByIndex = new Map(failures.map((failure) => [failure.sourceIndex, failure]));
  const out: unknown[] = [];
  let rowCursor = 0;
  for (let i = 0; i < rows.length + failures.length; i += 1) {
    const failure = failureByIndex.get(i);
    if (failure) {
      out.push(failure.raw);
    } else {
      out.push(serializeZeptoRow(rows[rowCursor++]));
    }
  }
  return out;
}

export function formatZeptoParseFailuresMessage(failures: ZeptoParseFailure[]): string {
  if (!failures.length) {
    return "";
  }
  const count = failures.length;
  const noun =
    count === 1 ? "строку" : count >= 2 && count <= 4 ? "строки" : "строк";
  const details = failures
    .map((failure) => {
      const pos = `#${failure.sourceIndex + 1}`;
      return failure.rowId != null ? `${pos}, id ${failure.rowId}` : `${pos}, без id`;
    })
    .join("; ");
  return `Не удалось разобрать ${count} ${noun} zepto: ${details}`;
}

export function createEmptyZeptoRow(rowId: number): ZeptoRowEdit {
  return {
    type: 1,
    rowId,
    arabic: "",
    russian: "",
    translationEn: "",
    translationFr: "",
    ruleId: "",
    taskAnswer: "",
    taskSentence: "",
    ttsVoice: "",
  };
}

export function moveZeptoRow(
  rows: ZeptoRowEdit[],
  fromIndex: number,
  toIndex: number,
): ZeptoRowEdit[] | null {
  if (fromIndex === toIndex) {
    return null;
  }
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= rows.length || toIndex >= rows.length) {
    return null;
  }
  const next = rows.slice();
  const [row] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, row!);
  return next;
}

export function isValidZeptoRowId(value: number): boolean {
  return Number.isInteger(value) && value > 0;
}

export function isZeptoRowIdTaken(
  rows: ZeptoRowEdit[],
  rowId: number,
  ownerRowId?: number,
): boolean {
  return rows.some((row) => row.rowId === rowId && row.rowId !== ownerRowId);
}

export function nextZeptoRowId(rows: ZeptoRowEdit[]): number {
  const used = new Set<number>();
  for (const row of rows) {
    if (isValidZeptoRowId(row.rowId)) {
      used.add(row.rowId);
    }
  }
  let candidate = 1;
  while (used.has(candidate)) {
    candidate += 1;
  }
  return candidate;
}

export type ProofreadKind = "source" | "translation";

export type ProofreadField =
  | "title_arabic"
  | "title_russian"
  | "type"
  | "arabic"
  | "russian"
  | "taskAnswer"
  | "taskSentence";

export type ProofreadTranslationReference = {
  zepto_type: number | null;
  zepto_kind: string;
  arabic_raw: string;
  arabic_for_translation: string;
  arabic_prompt?: string;
  correct_arabic?: string[];
  built_arabic?: string;
  russian_structure?: string;
};

export type ProofreadItem = {
  id: string;
  rowId: number | null;
  field: ProofreadField;
  text: string;
  sourceText?: string;
  rowType?: number;
  exerciseKind?: string;
  translationRules?: string;
  correctAnswersArabic?: string[];
  russianHasExerciseMarkup?: boolean;
  translationReference?: ProofreadTranslationReference;
  arabicResolved?: string;
};

export type ProofreadSuggestion = {
  id: string;
  rowId: number | null;
  field: ProofreadField;
  current: string;
  suggested: string;
  note?: string;
};

const SOURCE_PROOFREAD_FIELDS = new Set<ProofreadField>([
  "title_arabic",
  "arabic",
  "taskAnswer",
  "taskSentence",
]);

const TRANSLATION_PROOFREAD_FIELDS = new Set<ProofreadField>([
  "title_russian",
  "russian",
]);

export function proofreadFieldsForKind(kind: ProofreadKind): Set<ProofreadField> {
  return kind === "source" ? SOURCE_PROOFREAD_FIELDS : TRANSLATION_PROOFREAD_FIELDS;
}

export function proofreadItemId(rowId: number | null, field: ProofreadField): string {
  return rowId == null ? `title:${field}` : `${rowId}:${field}`;
}

export function russianHasExerciseMarkup(text: string): boolean {
  if (!text.trim()) {
    return false;
  }
  return /\|[^|]*[+-][^|]*\|/.test(text) || /\|\*/.test(text) || /\|\|/.test(text);
}

export function selectCorrectAnswersFromArabic(arabic: string): string[] {
  const answers: string[] = [];
  for (const marker of arabic.matchAll(/\|([^|]+)\|/g)) {
    const markerValue = marker[1] || "";
    for (const match of markerValue.matchAll(/\+([^+-]+)/g)) {
      const word = match[1]?.trim();
      if (word) {
        answers.push(word);
      }
    }
  }
  return answers;
}

export function arabicPromptFromSelect(arabic: string): string {
  const pipe = arabic.indexOf("|");
  return (pipe >= 0 ? arabic.slice(0, pipe) : arabic).trim();
}

export function buildProofreadTranslationReference(
  row: ZeptoRowEdit | null,
  sourceArabic: string,
): ProofreadTranslationReference {
  const raw = sourceArabic.trim();
  const ref: ProofreadTranslationReference = {
    zepto_type: row?.type ?? null,
    zepto_kind: row != null ? zeptoTypeLabel(row.type) : "Заголовок",
    arabic_raw: raw,
    arabic_for_translation: raw,
  };
  if (!row) {
    return ref;
  }
  switch (row.type) {
    case 3: {
      const filled =
        row.taskSentence && row.taskAnswer
          ? fillTaskBlanks(row.taskSentence, row.taskAnswer)
          : "";
      if (filled) {
        ref.built_arabic = filled;
        ref.arabic_for_translation = filled;
        ref.russian_structure =
          "One natural Russian sentence translating built_arabic (blanks filled). No # markup.";
      }
      break;
    }
    case 5:
      ref.built_arabic = stripBuildMarkers(raw);
      ref.arabic_for_translation = ref.built_arabic;
      ref.russian_structure =
        "One natural Russian sentence for built_arabic. No |*...*|| markup.";
      break;
    case 6: {
      ref.arabic_prompt = arabicPromptFromSelect(raw);
      ref.correct_arabic = selectCorrectAnswersFromArabic(raw);
      ref.arabic_for_translation = ref.arabic_prompt || raw;
      if (ref.correct_arabic.length) {
        ref.russian_structure =
          "Exercise prompt from arabic_prompt + correct answer in Russian (translate correct_arabic). No option markup.";
      } else {
        ref.russian_structure = "Natural Russian translation of arabic_prompt.";
      }
      break;
    }
    default:
      ref.russian_structure = "Natural Russian translation of arabic_for_translation.";
  }
  return ref;
}

export function arabicResolvedForProofread(
  row: ZeptoRowEdit | null,
  field: ProofreadField,
  text: string,
): string {
  const trimmed = text.trim();
  if (!row) {
    return trimmed;
  }
  if (row.type === 3) {
    return trimmed;
  }
  if (field !== "arabic") {
    return trimmed;
  }
  switch (row.type) {
    case 5:
      return stripBuildMarkers(trimmed);
    case 6: {
      const prompt = arabicPromptFromSelect(trimmed);
      const correct = selectCorrectAnswersFromArabic(trimmed);
      if (correct.length) {
        return `${prompt} → ${correct.join(", ")}`;
      }
      return prompt || trimmed;
    }
    default:
      return trimmed;
  }
}

export function proofreadTranslationRules(row: ZeptoRowEdit | null, field: ProofreadField): string {
  if (field === "title_russian") {
    return "Final Russian lesson title for learners. No Arabic script, no exercise markup (|, +, -, #, *).";
  }
  switch (row?.type) {
    case 1:
      return "Final Russian gloss of the Arabic word or phrase (arabic_for_translation). No markup.";
    case 2:
      return "Final Russian translation of the Arabic sentence (arabic_for_translation). No markup.";
    case 3:
      return "Final Russian shown under the task: translate built_arabic (blanks already filled). No # markup or Arabic script.";
    case 4:
      return "Final Russian translation of the dialog line (arabic_for_translation). No markup.";
    case 5:
      return [
        "Final Russian for the learner: one natural sentence matching built_arabic.",
        "Arabic build markup |*part*part*||suffix| must NOT appear in Russian.",
      ].join(" ");
    case 6:
      return [
        "Final Russian for the learner: translate arabic_prompt as the exercise text,",
        "then give the correct answer in Russian (translate words from correct_arabic).",
        "No |, +, -, or option lists in Russian.",
      ].join(" ");
    default:
      return "Final Russian translation for learners. No Arabic script or exercise markup.";
  }
}

function proofreadTranslationItemMeta(
  row: ZeptoRowEdit | null,
  field: ProofreadField,
  text: string,
  sourceText?: string,
): Pick<
  ProofreadItem,
  | "exerciseKind"
  | "translationRules"
  | "correctAnswersArabic"
  | "russianHasExerciseMarkup"
  | "translationReference"
> {
  if (field !== "title_russian" && field !== "russian") {
    return {};
  }
  const meta: Pick<
    ProofreadItem,
    | "exerciseKind"
    | "translationRules"
    | "correctAnswersArabic"
    | "russianHasExerciseMarkup"
    | "translationReference"
  > = {
    translationRules: proofreadTranslationRules(row, field),
    russianHasExerciseMarkup: russianHasExerciseMarkup(text),
  };
  if (sourceText?.trim()) {
    meta.translationReference = buildProofreadTranslationReference(row, sourceText);
    meta.correctAnswersArabic = meta.translationReference.correct_arabic;
  }
  if (row) {
    meta.exerciseKind = zeptoTypeLabel(row.type);
  } else {
    meta.exerciseKind = "Заголовок";
  }
  return meta;
}

export function collectProofreadItems(
  kind: ProofreadKind,
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  const items: ProofreadItem[] = [];
  if (kind === "source") {
    const title = titleArabic.trim();
    if (title) {
      items.push({
        id: proofreadItemId(null, "title_arabic"),
        rowId: null,
        field: "title_arabic",
        text: title,
        exerciseKind: "Заголовок",
        arabicResolved: title,
      });
    }
  } else {
    const titleAr = titleArabic.trim();
    const titleRu = titleRussian.trim();
    if (titleRu) {
      items.push({
        id: proofreadItemId(null, "title_russian"),
        rowId: null,
        field: "title_russian",
        text: titleRu,
        sourceText: titleAr || undefined,
        ...proofreadTranslationItemMeta(null, "title_russian", titleRu, titleAr),
      });
    } else if (titleAr) {
      items.push({
        id: proofreadItemId(null, "title_russian"),
        rowId: null,
        field: "title_russian",
        text: "",
        sourceText: titleAr,
        ...proofreadTranslationItemMeta(null, "title_russian", "", titleAr),
      });
    }
  }

  for (const row of rows) {
    if (kind === "source") {
      if (row.type === 3) {
        for (const field of ["taskAnswer", "taskSentence"] as const) {
          const text = row[field].trim();
          if (!text) {
            continue;
          }
          items.push({
            id: proofreadItemId(row.rowId, field),
            rowId: row.rowId,
            field,
            text,
            rowType: row.type,
            exerciseKind: zeptoTypeLabel(row.type),
            arabicResolved: arabicResolvedForProofread(row, field, text),
          });
        }
        continue;
      }
      const text = row.arabic.trim();
      if (!text) {
        continue;
      }
      items.push({
        id: proofreadItemId(row.rowId, "arabic"),
        rowId: row.rowId,
        field: "arabic",
        text,
        rowType: row.type,
        exerciseKind: zeptoTypeLabel(row.type),
        arabicResolved: arabicResolvedForProofread(row, "arabic", text),
      });
      continue;
    }

    if (row.type === 3) {
      const filled =
        row.taskSentence && row.taskAnswer
          ? fillTaskBlanks(row.taskSentence, row.taskAnswer)
          : "";
      const russian = row.russian.trim();
      if (russian) {
        items.push({
          id: proofreadItemId(row.rowId, "russian"),
          rowId: row.rowId,
          field: "russian",
          text: russian,
          sourceText: filled || undefined,
          rowType: row.type,
          ...proofreadTranslationItemMeta(row, "russian", russian, filled),
        });
      } else if (filled) {
        items.push({
          id: proofreadItemId(row.rowId, "russian"),
          rowId: row.rowId,
          field: "russian",
          text: "",
          sourceText: filled,
          rowType: row.type,
          ...proofreadTranslationItemMeta(row, "russian", "", filled),
        });
      }
      continue;
    }
    const arabic = row.arabic.trim();
    const russian = row.russian.trim();
    if (russian) {
      items.push({
        id: proofreadItemId(row.rowId, "russian"),
        rowId: row.rowId,
        field: "russian",
        text: russian,
        sourceText: arabic || undefined,
        rowType: row.type,
        ...proofreadTranslationItemMeta(row, "russian", russian, arabic),
      });
    } else if (arabic) {
      items.push({
        id: proofreadItemId(row.rowId, "russian"),
        rowId: row.rowId,
        field: "russian",
        text: "",
        sourceText: arabic,
        rowType: row.type,
        ...proofreadTranslationItemMeta(row, "russian", "", arabic),
      });
    }
  }

  return items;
}

export function collectAllProofreadItems(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  return [
    ...collectProofreadItems("source", rows, titleArabic, titleRussian),
    ...collectProofreadItems("translation", rows, titleArabic, titleRussian),
  ];
}

export function collectProofreadItemsForRow(
  rowId: number,
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadItem[] {
  return collectAllProofreadItems(rows, titleArabic, titleRussian).filter(
    (item) => item.rowId === rowId,
  );
}

export type ProofreadLessonZeptoPayload = {
  title_arabic: string;
  title_russian: string;
  zepto: unknown[];
  translation_hints?: ProofreadTranslationHint[];
};

export type ProofreadTranslationHint = {
  row_id: number | null;
  field: "title_russian" | "russian";
  zepto_type: number | null;
  translation_rules: string;
  arabic_for_translation?: string;
  built_arabic?: string;
  arabic_prompt?: string;
  correct_arabic?: string[];
  russian_structure?: string;
};

export function buildProofreadTranslationHints(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
  rowId?: number,
): ProofreadTranslationHint[] {
  const items = collectProofreadItems("translation", rows, titleArabic, titleRussian);
  const hints: ProofreadTranslationHint[] = [];
  for (const item of items) {
    if (item.field !== "title_russian" && item.field !== "russian") {
      continue;
    }
    if (rowId != null && item.rowId !== rowId) {
      continue;
    }
    const ref = item.translationReference;
    hints.push({
      row_id: item.rowId,
      field: item.field,
      zepto_type: item.rowType ?? null,
      translation_rules: item.translationRules ?? proofreadTranslationRules(
        item.rowId != null ? rows.find((row) => row.rowId === item.rowId) ?? null : null,
        item.field,
      ),
      ...(ref?.arabic_for_translation ? { arabic_for_translation: ref.arabic_for_translation } : {}),
      ...(ref?.built_arabic ? { built_arabic: ref.built_arabic } : {}),
      ...(ref?.arabic_prompt ? { arabic_prompt: ref.arabic_prompt } : {}),
      ...(ref?.correct_arabic?.length ? { correct_arabic: ref.correct_arabic } : {}),
      ...(ref?.russian_structure ? { russian_structure: ref.russian_structure } : {}),
    });
  }
  return hints;
}

export function buildProofreadLessonZeptoPayload(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
): ProofreadLessonZeptoPayload {
  try {
    return {
      title_arabic: titleArabic,
      title_russian: titleRussian,
      zepto: rowsToZepto(rows),
    };
  } catch {
    throw new Error("Не удалось собрать zepto");
  }
}

export function buildProofreadRowZeptoPayload(row: ZeptoRowEdit): ProofreadLessonZeptoPayload {
  try {
    return {
      title_arabic: "",
      title_russian: "",
      zepto: rowsToZepto([row]),
    };
  } catch {
    throw new Error("Не удалось собрать zepto");
  }
}

export function textHasArabicScript(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
}

export function isSelectZeptoArabic(text: string): boolean {
  return /\|[^|]*\+[^|]+\|/.test(text.trim());
}

/** Whether field text uses only markup allowed for the given zepto row type. */
export function zeptoProofreadFieldSyntaxValid(
  type: number,
  field: ProofreadField,
  text: string,
): boolean {
  const value = text.trim();
  if (!value) {
    return true;
  }
  if (!isKnownZeptoType(type)) {
    return false;
  }
  if (field === "type") {
    return isKnownZeptoType(Number(value));
  }
  if (field === "title_arabic") {
    return (
      !russianHasExerciseMarkup(value) &&
      !hasTaskBlankMarkup(value) &&
      !hasPipeCharacter(value)
    );
  }
  if (field === "title_russian" || field === "russian") {
    return !russianHasExerciseMarkup(value);
  }
  if (field === "taskSentence" || field === "taskAnswer") {
    if (type !== 3) {
      return false;
    }
    return (
      !hasPipeCharacter(value) &&
      !isSelectZeptoArabic(value) &&
      !hasBuildZeptoMarkup(value)
    );
  }
  if (field === "arabic") {
    switch (type) {
      case 1:
      case 2:
      case 4:
        return (
          !isSelectZeptoArabic(value) &&
          !hasBuildZeptoMarkup(value) &&
          !hasTaskBlankMarkup(value) &&
          !hasPipeCharacter(value)
        );
      case 5:
        return !isSelectZeptoArabic(value);
      case 6:
        return !hasBuildZeptoMarkup(value);
      default:
        return false;
    }
  }
  return true;
}

export function zeptoRowProofreadSyntaxValid(row: ZeptoRowEdit): boolean {
  if (!isKnownZeptoType(row.type)) {
    return false;
  }
  if (row.type === 3) {
    if (row.taskSentence.trim() && !zeptoProofreadFieldSyntaxValid(row.type, "taskSentence", row.taskSentence)) {
      return false;
    }
    if (row.taskAnswer.trim() && !zeptoProofreadFieldSyntaxValid(row.type, "taskAnswer", row.taskAnswer)) {
      return false;
    }
  } else if (row.type !== 7 && row.arabic.trim()) {
    if (!zeptoProofreadFieldSyntaxValid(row.type, "arabic", row.arabic)) {
      return false;
    }
  }
  if (row.russian.trim() && !zeptoProofreadFieldSyntaxValid(row.type, "russian", row.russian)) {
    return false;
  }
  return true;
}

export function zeptoTypeOptionLabel(type: number): string {
  return `${type} — ${zeptoTypeLabel(type)}`;
}

export function rowPatchForProofreadField(
  field: ProofreadField,
  value: string,
  row?: ZeptoRowEdit | null,
): Partial<ZeptoRowEdit> | null {
  if (field === "title_arabic" || field === "title_russian") {
    return null;
  }
  if (field === "type") {
    const nextType = Number(value);
    if (!isKnownZeptoType(nextType)) {
      return null;
    }
    return { type: nextType };
  }
  const patch: Partial<ZeptoRowEdit> = { [field]: value };
  if (field === "arabic" && row?.type === 2 && isSelectZeptoArabic(value)) {
    patch.type = 6;
  }
  return patch;
}

const PROOFREAD_NAV_FIELD_ORDER: ProofreadField[] = [
  "title_arabic",
  "title_russian",
  "taskAnswer",
  "taskSentence",
  "arabic",
  "russian",
];

function proofreadNavRank(suggestion: ProofreadSuggestion, rows: ZeptoRowEdit[]): number {
  if (suggestion.rowId == null) {
    return suggestion.field === "title_arabic" ? 0 : 1;
  }
  const fieldRank = PROOFREAD_NAV_FIELD_ORDER.indexOf(suggestion.field);
  const pos = zeptoRowListPosition(rows, suggestion.rowId);
  return 100_000 + pos * 10 + (fieldRank >= 0 ? fieldRank : 99);
}

export function compareProofreadSuggestions(
  a: ProofreadSuggestion,
  b: ProofreadSuggestion,
  rows: ZeptoRowEdit[],
): number {
  return proofreadNavRank(a, rows) - proofreadNavRank(b, rows);
}

export type LessonContentIssueKind = "missing_translation" | "missing_audio";

export type LessonContentIssue = {
  id: string;
  kind: LessonContentIssueKind;
  rowId: number | null;
  message: string;
};

export function contentIssueId(
  rowId: number | null,
  kind: LessonContentIssueKind,
): string {
  return rowId == null ? `issue:title:${kind}` : `issue:${rowId}:${kind}`;
}

export function collectLessonContentIssues(
  rows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
  rowHasAudio: (row: ZeptoRowEdit) => boolean,
): LessonContentIssue[] {
  const issues: LessonContentIssue[] = [];
  const titleAr = titleArabic.trim();
  const titleRu = titleRussian.trim();
  if (titleAr && !titleRu) {
    issues.push({
      id: contentIssueId(null, "missing_translation"),
      kind: "missing_translation",
      rowId: null,
      message: "Нет русского заголовка",
    });
  }

  for (const row of rows) {
    if (row.type === 7) {
      continue;
    }
    const arabic = row.arabic.trim();
    const russian = row.russian.trim();
    if (row.type === 3) {
      if (row.taskAnswer.trim() && row.taskSentence.trim() && !russian) {
        issues.push({
          id: contentIssueId(row.rowId, "missing_translation"),
          kind: "missing_translation",
          rowId: row.rowId,
          message: "Нет перевода",
        });
      }
    } else if (arabic && !russian) {
      issues.push({
        id: contentIssueId(row.rowId, "missing_translation"),
        kind: "missing_translation",
        rowId: row.rowId,
        message: "Нет перевода",
      });
    }
    if (getArabicTextForTts(row) && !rowHasAudio(row)) {
      issues.push({
        id: contentIssueId(row.rowId, "missing_audio"),
        kind: "missing_audio",
        rowId: row.rowId,
        message: "Нет аудиозаписи",
      });
    }
  }

  return issues;
}

function contentIssueNavRank(issue: LessonContentIssue, rows: ZeptoRowEdit[]): number {
  if (issue.rowId == null) {
    return issue.kind === "missing_translation" ? 1.5 : 99;
  }
  const pos = zeptoRowListPosition(rows, issue.rowId);
  const base = 100_000 + pos * 10;
  if (issue.kind === "missing_translation") {
    return base + 5.5;
  }
  if (issue.kind === "missing_audio") {
    return base + 6.5;
  }
  return base + 99;
}

export type EditorNavScrollTarget = {
  componentId: string;
  componentIndex: string;
  rowId: number | null;
};

export function editorNavScrollTarget(navId: string): EditorNavScrollTarget {
  if (navId === contentIssueId(null, "missing_translation")) {
    return {
      componentId: "editor_issue_title_translation",
      componentIndex: "0",
      rowId: null,
    };
  }
  if (navId.startsWith("issue:")) {
    const rest = navId.slice("issue:".length);
    if (rest.startsWith("title:")) {
      return {
        componentId: "editor_issue_title_translation",
        componentIndex: "0",
        rowId: null,
      };
    }
    const sep = rest.indexOf(":");
    const rowStr = rest.slice(0, sep);
    const kind = rest.slice(sep + 1) as LessonContentIssueKind;
    const rowId = Number(rowStr);
    if (kind === "missing_audio") {
      return {
        componentId: "editor_issue_row_audio",
        componentIndex: rowStr,
        rowId,
      };
    }
    return {
      componentId: "editor_issue_row_translation",
      componentIndex: rowStr,
      rowId,
    };
  }
  if (navId.startsWith("title:")) {
    const field = navId.slice("title:".length);
    return {
      componentId: `editor_proofread_title_${field}`,
      componentIndex: "0",
      rowId: null,
    };
  }
  const sep = navId.indexOf(":");
  const rowStr = navId.slice(0, sep);
  const field = navId.slice(sep + 1);
  return {
    componentId: `editor_proofread_${field}`,
    componentIndex: rowStr,
    rowId: Number(rowStr),
  };
}

export function buildProofreadNavIds(
  suggestions: Record<string, ProofreadSuggestion>,
  rows: ZeptoRowEdit[],
): string[] {
  return Object.values(suggestions)
    .sort((a, b) => compareProofreadSuggestions(a, b, rows))
    .map((item) => item.id);
}

export function buildContentIssueNavIds(
  issues: Record<string, LessonContentIssue>,
  rows: ZeptoRowEdit[],
): string[] {
  return Object.values(issues)
    .sort((a, b) => contentIssueNavRank(a, rows) - contentIssueNavRank(b, rows) || a.id.localeCompare(b.id))
    .map((item) => item.id);
}

export type LessonRuleRef = {
  rowId: number;
  rowNumber: number;
  ruleId: string;
};

/** Type-7 zepto rows in lesson order (rules interleaved with exercises). */
export function collectLessonRuleRefs(rows: ZeptoRowEdit[]): LessonRuleRef[] {
  const refs: LessonRuleRef[] = [];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (row.type !== 7) {
      continue;
    }
    refs.push({
      rowId: row.rowId,
      rowNumber: index + 1,
      ruleId: row.ruleId.trim(),
    });
  }
  return refs;
}
